import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { SketchPicker } from "react-color";

const EditCategory = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get("id");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    catTitle: "",
    description: "",
    taxonomy: "video",
    catid: "",
    catchild: "",
    grandchild: "",
    isLeaf: false,
    premium: false,
    color: "#ff0000", // Add this default to ensure SketchPicker doesn't break
  });

  const [terms, setTerms] = useState([]);
  const [children, setChildren] = useState([]);
  const [grandChildren, setGrandChildren] = useState([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLeafLocked, setIsLeafLocked] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Helper: find category by ID recursively
  const findById = (terms, id) => {
    for (const term of terms) {
      if (term.id === id) return term;
      if (term.children?.length) {
        const found = findById(term.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories`
        );
        const allTerms = res.data.data.terms;
        setTerms(allTerms); // includes leaf for hierarchy tracing

        const categoryRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/categories/${categoryId}`
        );
        const categoryData = categoryRes.data;

        let updatedForm = {
          catTitle: categoryData.name || "",
          description: categoryData.description || "",
          taxonomy: categoryData.taxonomy || "video",
          catid: "",
          catchild: "",
          grandchild: "",
          isLeaf: !!categoryData.taxonomy,
          premium: categoryData.premium || false,
          color: categoryData.color || "#ff0000", // ✅ Pull color from API
        };

        const hierarchy = [];
        let current = findById(allTerms, parseInt(categoryId));
        while (current?.parent_id) {
          const parent = findById(allTerms, current.parent_id);
          if (!parent) break;
          hierarchy.unshift(parent);
          current = parent;
        }

        if (hierarchy.length === 1) {
          updatedForm.catid = String(hierarchy[0].id);
        } else if (hierarchy.length === 2) {
          updatedForm.catid = String(hierarchy[0].id);
          updatedForm.catchild = String(hierarchy[1].id);
        } else if (hierarchy.length >= 3) {
          updatedForm.catid = String(hierarchy[0].id);
          updatedForm.catchild = String(hierarchy[1].id);
          updatedForm.grandchild = String(hierarchy[2].id);
        }

        const selectedParent = findById(allTerms, parseInt(updatedForm.catid));
        const selectedChild = selectedParent?.children?.find(
          (cat) => cat.id === parseInt(updatedForm.catchild)
        );

        const nonLeafOnly = (list) => list?.filter((cat) => !cat.taxonomy);
        setChildren(nonLeafOnly(selectedParent?.children || []));
        setGrandChildren(nonLeafOnly(selectedChild?.children || []));

        setFormData(updatedForm);
      } catch (error) {
        setError("Error fetching data: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) fetchData();
  }, [categoryId]);

  useEffect(() => {
    if (formData.catchild && children.length > 0) {
      const child = children.find(
        (cat) => cat.id === parseInt(formData.catchild)
      );
      setGrandChildren((child?.children || []).filter((cat) => !cat.taxonomy));
    } else {
      setGrandChildren([]);
    }
  }, [formData.catchild, children]);

  useEffect(() => {
    setIsLeafLocked(!!formData.grandchild);
    if (formData.grandchild) {
      setFormData((prev) => ({ ...prev, isLeaf: true }));
    }
  }, [formData.grandchild]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    if (!formData.catTitle.trim()) {
      setError("Please enter the Category name");
      setIsSubmitting(false);
      return;
    }

    const taxonomy = formData.isLeaf ? formData.taxonomy : null;
    const parentId = formData.grandchild
      ? formData.grandchild
      : formData.catchild
      ? formData.catchild
      : formData.catid
      ? formData.catid
      : null;

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/categories/${categoryId}`,
        {
          catTitle: formData.catTitle,
          description: formData.description,
          taxonomy,
          parent_id: parentId,
          premium: formData.premium,
          color: formData.color, // ✅ include color
        }
      );

      if (res.data.success) {
        navigate("/xl-admin/categories?action=updated");
      } else {
        throw new Error(res.data.message || "Failed to update category");
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h3 className="mb-4">Edit Category</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Category Name */}
          <div className="col-md-6 mb-3">
            <label htmlFor="catTitle" className="form-label">
              Category Name
            </label>
            <input
              type="text"
              name="catTitle"
              id="catTitle"
              className="form-control"
              value={formData.catTitle}
              onChange={handleChange}
              required
            />
          </div>

          {/* Parent */}
          <div className="col-md-6 mb-3">
            <label htmlFor="catid" className="form-label">
              Parent Category
            </label>
            <select
              name="catid"
              id="catid"
              className="form-select"
              value={formData.catid}
              onChange={handleChange}
            >
              <option value="">-- Select Parent --</option>
              {terms
                .filter((cat) => !cat.taxonomy)
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Child */}
          {children.length > 0 && (
            <div className="col-md-6 mb-3">
              <label htmlFor="catchild" className="form-label">
                Child Category
              </label>
              <select
                name="catchild"
                id="catchild"
                className="form-select"
                value={formData.catchild}
                onChange={handleChange}
              >
                <option value="">-- Select Child --</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Grandchild */}
          {grandChildren.length > 0 && (
            <div className="col-md-6 mb-3">
              <label htmlFor="grandchild" className="form-label">
                Grandchild Category
              </label>
              <select
                name="grandchild"
                id="grandchild"
                className="form-select"
                value={formData.grandchild}
                onChange={handleChange}
              >
                <option value="">-- Select Grandchild --</option>
                {grandChildren.map((gc) => (
                  <option key={gc.id} value={gc.id}>
                    {gc.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Color Picker */}
          <div className="col-md-6 mb-3 position-relative">
            <label className="form-label d-block">Category Color</label>
            <div
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{
                width: "50px",
                height: "40px",
                background: formData.color,
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Click to pick color"
            />

            {showColorPicker && (
              <div style={{ position: "absolute", zIndex: 10 }}>
                <SketchPicker
                  color={formData.color}
                  onChange={(color) =>
                    setFormData((prev) => ({ ...prev, color: color.hex }))
                  }
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="col-md-12 mb-3">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              className="form-control"
              rows="4"
              value={formData.description}
              onChange={handleChange}
            ></textarea>
          </div>

          {/* Is Leaf */}
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              id="isLeaf"
              name="isLeaf"
              className="form-check-input"
              checked={formData.isLeaf}
              onChange={handleChange}
              disabled={isLeafLocked}
            />
            <label htmlFor="isLeaf" className="form-check-label">
              Is this a leaf? (Can contain content)
            </label>
          </div>

          {/* Taxonomy */}
          {formData.isLeaf && (
            <div className="col-md-6 mb-3">
              <label htmlFor="taxonomy" className="form-label">
                Content Type
              </label>
              <select
                name="taxonomy"
                id="taxonomy"
                className="form-select"
                value={formData.taxonomy}
                onChange={handleChange}
              >
                <option value="video">Video</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>
          )}

          {/* Premium */}
          {formData.isLeaf && (
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                id="premium"
                name="premium"
                className="form-check-input"
                checked={formData.premium}
                onChange={handleChange}
              />
              <label htmlFor="premium" className="form-check-label">
                Premium
              </label>
            </div>
          )}
        </div>

        <div className="text-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Category"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCategory;
